import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import {
  Activity, ArrowRight, Bell, BellOff, BellRing, Brain, Check, CheckCheck, ChevronRight, Clock,
  ExternalLink, FileBarChart, FileCheck, Lightbulb, Loader2, MessageSquare, Radar, Send,
  ShieldCheck, Sparkles, TrendingDown, TrendingUp, X, Zap,
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

// ── Severity + category vocab ────────────────────────────────────────────────
const SEV = {
  critical: { dot: 'bg-rose-500',    text: 'text-rose-500',    ring: 'border-rose-500/30 bg-rose-500/5' },
  warn:     { dot: 'bg-amber-500',   text: 'text-amber-500',   ring: 'border-amber-500/30 bg-amber-500/5' },
  success:  { dot: 'bg-emerald-500', text: 'text-emerald-500', ring: 'border-emerald-500/30 bg-emerald-500/5' },
  info:     { dot: 'bg-sky-500',     text: 'text-sky-500',     ring: 'border-sky-500/30 bg-sky-500/5' },
};
const sev = (s) => SEV[s] || SEV.info;

const CAT_ICON = {
  ops: Brain, seo: Radar, coach: TrendingUp, message: MessageSquare,
  reconciliation: Activity, invoice: FileCheck, kyc: ShieldCheck, time: Clock,
  signature: FileCheck, health: Activity, system: Sparkles,
};
const catIcon = (c) => CAT_ICON[c] || Sparkles;

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24); return `${d}d`;
};

// Whitelisted agent functions the one-click pills may invoke.
export const AGENT_FNS = new Set(['ops-analyst-weekly', 'seo-trend-radar', 'generate-coaching-suggestions', 'platform-swot']);

const TopAlertStrip = ({
  goTo,
  onOpenInvoices,
  unreadCount = 0,
  pendingInvoiceCount = 0,
  pendingIdReviewCount = 0,
  pendingTimeRequestCount = 0,
}) => {
  const { toast } = useToast();
  const [alerts, setAlerts] = useState([]);
  const [tableReady, setTableReady] = useState(true);
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);          // ticker rotation + relative-time refresh
  const [busyId, setBusyId] = useState(null);    // alert id whose action is running
  const [running, setRunning] = useState(null);  // footer agent currently running
  const [reportAlert, setReportAlert] = useState(null); // alert whose report modal is open
  const [reportReads, setReportReads] = useState({});   // message_id -> { subcontractor_id: read_at }
  const [resending, setResending] = useState(null);     // message_id being re-sent
  const channelRef = useRef(null);
  const panelRef = useRef(null);

  const unresolved = useMemo(() => alerts.filter((a) => !a.resolved_at), [alerts]);
  const unread = useMemo(() => unresolved.filter((a) => !a.read_at), [unresolved]);

  const systemStatus = useMemo(() => {
    if (unresolved.some((a) => a.severity === 'critical')) return 'critical';
    if (unresolved.some((a) => a.severity === 'warn')
        || pendingInvoiceCount + pendingIdReviewCount + pendingTimeRequestCount > 0) return 'degraded';
    return 'operational';
  }, [unresolved, pendingInvoiceCount, pendingIdReviewCount, pendingTimeRequestCount]);

  // ── Fetch feed (tolerant: if the table/RPC isn't deployed yet, degrade quietly) ──
  const fetchFeed = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('admin_alerts_feed', { p_limit: 60, p_include_resolved: false });
      if (error) throw error;
      setAlerts(Array.isArray(data) ? data : []);
      setTableReady(true);
    } catch (_e) {
      setTableReady(false);
      setAlerts([]);
    }
  }, []);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  // ── Realtime: any change to admin_alerts refreshes the feed ──
  useEffect(() => {
    const ch = supabase
      .channel('admin-alerts-strip')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'admin_alerts' }, () => fetchFeed())
      .subscribe();
    channelRef.current = ch;
    return () => { supabase.removeChannel(ch); channelRef.current = null; };
  }, [fetchFeed]);

  // ── Ticker rotation + relative-time refresh every 5s ──
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(i);
  }, []);

  // ── Close panel on outside click / escape ──
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const markRead = useCallback(async (id) => {
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, read_at: a.read_at || new Date().toISOString() } : a)));
    try { await supabase.rpc('mark_admin_alert_read', { p_id: id }); } catch (_e) { /* best-effort */ }
  }, []);

  const resolveAlert = useCallback(async (id) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
    try { await supabase.rpc('resolve_admin_alert', { p_id: id }); } catch (_e) { fetchFeed(); }
  }, [fetchFeed]);

  const markAllRead = useCallback(async () => {
    setAlerts((prev) => prev.map((a) => ({ ...a, read_at: a.read_at || new Date().toISOString() })));
    try { await supabase.rpc('mark_all_admin_alerts_read'); } catch (_e) { /* best-effort */ }
  }, []);

  // ── Report modal: live read receipts for the dispatched crew reports ──
  useEffect(() => {
    const ids = (reportAlert?.meta?.report?.crews || []).map((c) => c.message_id).filter(Boolean);
    if (!ids.length) { setReportReads({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('message_recipients')
          .select('message_id, subcontractor_id, read_at')
          .in('message_id', ids);
        if (cancelled) return;
        const map = {};
        (data || []).forEach((r) => {
          (map[r.message_id] = map[r.message_id] || {})[r.subcontractor_id] = r.read_at;
        });
        setReportReads(map);
      } catch (_e) { if (!cancelled) setReportReads({}); }
    })();
    return () => { cancelled = true; };
  }, [reportAlert]);

  const resendReport = useCallback(async (messageId) => {
    if (!messageId) return;
    setResending(messageId);
    try {
      const { error } = await supabase.functions.invoke('send-new-message-alert', { body: { record: { id: messageId } } });
      if (error) throw error;
      toast({ title: 'Report re-sent', description: 'The alert was dispatched again.', variant: 'info' });
    } catch (e) {
      toast({ title: 'Resend failed', description: e?.message || 'Please try again.', variant: 'destructive' });
    } finally { setResending(null); }
  }, [toast]);

  // ── One-click pill actions ──
  const runAction = useCallback(async (alert) => {
    const p = alert.action_payload || {};
    try {
      switch (alert.action_type) {
        case 'open_report':
          setReportAlert(alert); markRead(alert.id);
          return;
        case 'open_route':
          if (p.path) { goTo?.(p.path); setOpen(false); markRead(alert.id); }
          return;
        case 'open_url':
          if (p.url) { window.open(p.url, '_blank', 'noopener'); markRead(alert.id); }
          return;
        case 'open_invoice':
          if (p.invoice_id) { goTo?.(`/admin/dashboard/invoicing?openInvoice=${p.invoice_id}`); setOpen(false); markRead(alert.id); }
          return;
        case 'dispatch_coach':
          goTo?.('/admin/dashboard/messages'); setOpen(false); markRead(alert.id);
          return;
        case 'generate_article': {
          setBusyId(alert.id);
          const { data, error } = await supabase.functions.invoke('seo-content-engine', {
            body: { phase: 'auto', audience: p.audience || 'drivers', topic: p.topic || alert.title, review: true },
          });
          if (error || data?.error) throw new Error(error?.message || data?.error);
          toast({ title: 'Drafting article', description: `“${p.topic || alert.title}” is being written into the review queue.`, variant: 'info' });
          await resolveAlert(alert.id);
          return;
        }
        case 'run_agent': {
          const fn = p.fn;
          if (!AGENT_FNS.has(fn)) return;
          setBusyId(alert.id);
          const { data, error } = await supabase.functions.invoke(fn, { body: p.body || {} });
          if (error || data?.error) throw new Error(error?.message || data?.error);
          toast({ title: 'Agent running', description: `${fn} is refreshing its findings.`, variant: 'info' });
          await markRead(alert.id);
          fetchFeed();
          return;
        }
        default:
          markRead(alert.id);
      }
    } catch (e) {
      toast({ title: 'Action failed', description: e?.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  }, [goTo, markRead, resolveAlert, fetchFeed, toast]);

  // ── Footer: run agents on demand ──
  const runAgent = useCallback(async (fn, label) => {
    setRunning(fn);
    try {
      const { data, error } = await supabase.functions.invoke(fn, { body: {} });
      if (error || data?.error) throw new Error(error?.message || data?.error);
      toast({ title: `${label} started`, description: 'New findings will appear here shortly.', variant: 'info' });
      setTimeout(fetchFeed, 1500);
    } catch (e) {
      toast({ title: `${label} unavailable`, description: e?.message || 'The agent may not be deployed yet.', variant: 'destructive' });
    } finally {
      setRunning(null);
    }
  }, [fetchFeed, toast]);

  // Rotating ticker headline (top unresolved alerts)
  const tickerPool = unresolved.slice(0, 6);
  const headline = tickerPool.length ? tickerPool[tick % tickerPool.length] : null;

  const statusLabel = systemStatus === 'critical' ? 'Attention needed'
    : systemStatus === 'degraded' ? 'Live activity' : 'All systems operational';
  const statusDot = systemStatus === 'critical' ? 'bg-rose-500'
    : systemStatus === 'degraded' ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    // top offset = header height (4rem) + iOS status-bar inset, so the strip
    // pins flush under the header instead of sliding beneath it.
    <div
      className="sticky z-20 border-b border-border bg-card/70 backdrop-blur-xl"
      style={{ top: 'calc(4rem + env(safe-area-inset-top))' }}
    >
      <div className="flex h-10 items-center gap-3 px-4 sm:px-6 lg:px-8">
        {/* System status */}
        <div className="flex shrink-0 items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            {systemStatus !== 'operational' && (
              <span className={cn('absolute inline-flex h-full w-full animate-ping rounded-full opacity-60', statusDot)} />
            )}
            <span className={cn('relative inline-flex h-2.5 w-2.5 rounded-full', statusDot)} />
          </span>
          <span className="hidden text-xs font-semibold text-foreground sm:inline">{statusLabel}</span>
        </div>

        {/* Live ticker */}
        <div className="relative min-w-0 flex-1 overflow-hidden">
          <AnimatePresence mode="wait">
            {headline ? (
              <motion.button
                key={headline.id + ':' + (tick % Math.max(1, tickerPool.length))}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.25 }}
                onClick={() => setOpen(true)}
                className="flex w-full items-center gap-2 text-left"
              >
                <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', sev(headline.severity).dot)} />
                <span className="truncate text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{headline.title}</span>
                  {headline.body ? <span className="hidden md:inline"> — {headline.body}</span> : null}
                </span>
              </motion.button>
            ) : (
              <span className="truncate text-xs text-muted-foreground">
                Cockpit online — agents report findings here as they run.
              </span>
            )}
          </AnimatePresence>
        </div>

        {/* Quick signals */}
        <div className="flex shrink-0 items-center gap-1.5">
          {unreadCount > 0 && (
            <button
              onClick={() => goTo?.('/admin/dashboard/messages')}
              className="hidden h-7 items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-2 text-[11px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 dark:border-indigo-800/50 dark:bg-indigo-900/20 dark:text-indigo-400 md:flex"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              <span>{unreadCount}</span>
            </button>
          )}

          {/* Notification center trigger */}
          <button
            onClick={() => setOpen((o) => !o)}
            aria-label="Notifications"
            className={cn(
              'relative grid h-7 w-7 place-items-center rounded-lg border transition-colors',
              open ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border bg-background text-muted-foreground hover:text-foreground'
            )}
          >
            {unread.length > 0 ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
            {unread.length > 0 && (
              <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
                {unread.length > 99 ? '99+' : unread.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Notification center ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.16 }}
            className="absolute right-2 top-[calc(100%+6px)] z-50 flex max-h-[72vh] w-[min(420px,calc(100vw-1rem))] flex-col overflow-hidden rounded-2xl border border-border bg-popover shadow-shell sm:right-4 lg:right-8"
          >
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">Control tower</span>
              <span className="text-xs text-muted-foreground">{unresolved.length} active</span>
              <div className="ml-auto flex items-center gap-1">
                {unread.length > 0 && (
                  <button onClick={markAllRead} className="flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground">
                    <CheckCheck className="h-3.5 w-3.5" /> Mark all read
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="grid h-6 w-6 place-items-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="premium-scrollbar flex-1 overflow-y-auto">
              {!tableReady ? (
                <div className="px-4 py-10 text-center">
                  <Activity className="mx-auto h-6 w-6 text-muted-foreground/50" />
                  <p className="mt-2 text-sm font-medium text-foreground">Cockpit feed not deployed yet</p>
                  <p className="mt-1 text-xs text-muted-foreground">Apply the <code>admin_alerts</code> migration to switch it on.</p>
                </div>
              ) : unresolved.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <CheckCheck className="mx-auto h-6 w-6 text-emerald-500/70" />
                  <p className="mt-2 text-sm font-medium text-foreground">You're all caught up</p>
                  <p className="mt-1 text-xs text-muted-foreground">Run a brief below to pull fresh findings.</p>
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {unresolved.map((a) => {
                    const Icon = catIcon(a.category);
                    const s = sev(a.severity);
                    const busy = busyId === a.id;
                    return (
                      <li key={a.id} className={cn('px-4 py-3 transition-colors hover:bg-muted/40', !a.read_at && 'bg-primary/[0.03]')}>
                        <div className="flex gap-3">
                          <span className={cn('mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg border', s.ring)}>
                            <Icon className={cn('h-4 w-4', s.text)} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start gap-2">
                              <p className="min-w-0 flex-1 text-sm font-semibold text-foreground">{a.title}</p>
                              {!a.read_at && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                            </div>
                            {a.body && <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{a.body}</p>}
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">{a.source || a.category}</span>
                              <span className="text-[10px] text-muted-foreground/60">· {timeAgo(a.created_at)}</span>
                              <div className="ml-auto flex items-center gap-1">
                                {a.action_type && a.action_type !== 'none' && (
                                  <button
                                    onClick={() => runAction(a)}
                                    disabled={busy}
                                    className="flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
                                  >
                                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                                    {a.action_label || 'Do it'}
                                  </button>
                                )}
                                <button
                                  onClick={() => resolveAlert(a.id)}
                                  title="Dismiss"
                                  className="grid h-6 w-6 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                                >
                                  <Check className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Footer: run the reporting agents on demand */}
            <div className="flex items-center gap-2 border-t border-border bg-card/60 px-3 py-2">
              <button
                onClick={() => runAgent('ops-analyst-weekly', 'Weekly brief')}
                disabled={running === 'ops-analyst-weekly'}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-2 py-1.5 text-[11px] font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-60"
              >
                {running === 'ops-analyst-weekly' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Brain className="h-3.5 w-3.5" />}
                Weekly brief
              </button>
              <button
                onClick={() => runAgent('seo-trend-radar', 'SEO radar')}
                disabled={running === 'seo-trend-radar'}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-2 py-1.5 text-[11px] font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-60"
              >
                {running === 'seo-trend-radar' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Radar className="h-3.5 w-3.5" />}
                Scan trends
              </button>
              <button
                onClick={() => runAgent('platform-swot', 'Daily SWOT')}
                disabled={running === 'platform-swot'}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-2 py-1.5 text-[11px] font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-60"
              >
                {running === 'platform-swot' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                Daily SWOT
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Report modal — portaled to <body> so the fixed overlay escapes the
           strip's backdrop-filter containing block + z-20 stacking context
           (it was rendering behind the drawer/page). ── */}
      {createPortal(
        <AnimatePresence>
        {reportAlert && (() => {
          const report = reportAlert.meta?.report || {};
          const crews = Array.isArray(report.crews) ? report.crews : [];
          const isSwot = report.kind === 'platform_swot';
          const swot = report.swot || {};
          const SWOT_Q = [
            { key: 'strengths', label: 'Strengths', cls: 'border-emerald-500/30 bg-emerald-500/5', tone: 'text-emerald-600' },
            { key: 'weaknesses', label: 'Weaknesses', cls: 'border-rose-500/30 bg-rose-500/5', tone: 'text-rose-600' },
            { key: 'opportunities', label: 'Opportunities', cls: 'border-sky-500/30 bg-sky-500/5', tone: 'text-sky-600' },
            { key: 'threats', label: 'Threats', cls: 'border-amber-500/30 bg-amber-500/5', tone: 'text-amber-600' },
          ];
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-slate-950/50 p-4 backdrop-blur-sm sm:p-8"
              onClick={() => setReportAlert(null)}
            >
              <motion.div
                initial={{ opacity: 0, y: 12, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 12, scale: 0.98 }}
                transition={{ duration: 0.18 }}
                onClick={(e) => e.stopPropagation()}
                className="my-auto w-full max-w-2xl overflow-hidden rounded-2xl border border-border bg-popover shadow-shell"
              >
                <div className="flex items-start gap-3 border-b border-border px-5 py-4">
                  <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-rose-500/30 bg-rose-500/5">
                    <FileBarChart className="h-4.5 w-4.5 text-rose-500" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold text-foreground">{reportAlert.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {(isSwot && swot.headline) || report.summary || reportAlert.body}
                      {report.week ? ` · week of ${report.week}` : ''}
                    </p>
                  </div>
                  <button onClick={() => setReportAlert(null)} className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground">
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="premium-scrollbar max-h-[64vh] space-y-3 overflow-y-auto p-5">
                  {isSwot ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {SWOT_Q.map((q) => {
                        const items = Array.isArray(swot[q.key]) ? swot[q.key] : [];
                        return (
                          <div key={q.key} className={cn('rounded-xl border p-4', q.cls)}>
                            <p className={cn('mb-2 text-xs font-bold uppercase tracking-wide', q.tone)}>{q.label}</p>
                            {items.length === 0 ? (
                              <p className="text-xs text-muted-foreground">—</p>
                            ) : (
                              <ul className="space-y-2">
                                {items.map((it, i) => (
                                  <li key={i} className="text-xs leading-relaxed text-foreground">
                                    <span className="font-medium">{typeof it === 'string' ? it : it.point}</span>
                                    {it && typeof it === 'object' && it.evidence
                                      ? <span className="mt-0.5 block text-[11px] text-muted-foreground">{it.evidence}</span> : null}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : crews.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">No detail was attached to this report.</p>
                  ) : crews.map((c, i) => (
                    <div key={c.crew + i} className="rounded-xl border border-border bg-card/60 p-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">{c.crew}</span>
                        {c.grade && (
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                            {c.grade}
                          </span>
                        )}
                        <div className="ml-auto flex items-center gap-1.5 text-sm">
                          <TrendingDown className="h-4 w-4 text-rose-500" />
                          <span className="font-medium text-muted-foreground">{c.from}</span>
                          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60" />
                          <span className="font-semibold text-rose-500">{c.to}</span>
                          {Number.isFinite(c.delta) && (
                            <span className="ml-1 rounded-md bg-rose-500/10 px-1.5 py-0.5 text-[11px] font-bold text-rose-500">
                              {c.delta > 0 ? '+' : ''}{c.delta}
                            </span>
                          )}
                        </div>
                      </div>

                      {Array.isArray(c.drops) && c.drops.length > 0 && (
                        <div className="mt-3">
                          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">What slipped</p>
                          <div className="flex flex-wrap gap-1.5">
                            {c.drops.map((d, j) => (
                              <span key={j} className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2 py-1 text-[11px]">
                                <span className="font-medium text-foreground">{d.name}</span>
                                <span className="text-muted-foreground">{d.value_from ?? '—'}</span>
                                <ArrowRight className="h-3 w-3 text-muted-foreground/50" />
                                <span className="font-semibold text-rose-500">{d.value_to ?? '—'}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {c.tip && (
                        <div className="mt-3 flex gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3">
                          <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          <p className="text-xs leading-relaxed text-foreground">{c.tip}</p>
                        </div>
                      )}

                      {/* Delivery: who got it, push-enabled, read/unread + resend */}
                      {Array.isArray(c.recipients) && c.recipients.length > 0 ? (
                        <div className="mt-3 rounded-lg border border-border bg-background p-3">
                          <div className="mb-1.5 flex items-center gap-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                              Sent to {c.sent || c.recipients.length} driver{(c.sent || c.recipients.length) === 1 ? '' : 's'}
                            </p>
                            {c.message_id && (
                              <button
                                onClick={() => resendReport(c.message_id)}
                                disabled={resending === c.message_id}
                                className="ml-auto flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-60"
                              >
                                {resending === c.message_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Resend
                              </button>
                            )}
                          </div>
                          <div className="space-y-1">
                            {c.recipients.map((r) => {
                              const readAt = reportReads[c.message_id]?.[r.id];
                              return (
                                <div key={r.id} className="flex items-center gap-2 text-xs">
                                  <span className="min-w-0 flex-1 truncate text-foreground">{r.name}</span>
                                  <span title={r.push_enabled ? 'Push notifications enabled' : 'Push not enabled'}>
                                    {r.push_enabled
                                      ? <BellRing className="h-3.5 w-3.5 text-emerald-500" />
                                      : <BellOff className="h-3.5 w-3.5 text-muted-foreground/40" />}
                                  </span>
                                  {readAt ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-600">
                                      <Check className="h-3 w-3" /> Read
                                    </span>
                                  ) : (
                                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">Unread</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : c.message_id === null && 'recipients' in c ? (
                        <p className="mt-3 text-[11px] text-muted-foreground/70">Not dispatched — no active drivers on this crew.</p>
                      ) : null}
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-2 border-t border-border bg-card/60 px-5 py-3">
                  <span className="text-[11px] text-muted-foreground">Source: {reportAlert.source || 'Weekly Ops Analyst'}</span>
                  <button
                    onClick={() => {
                      const path = reportAlert.action_payload?.path || '/admin/dashboard/performance';
                      goTo?.(path); setReportAlert(null); setOpen(false);
                    }}
                    className="ml-auto flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                  >
                    <TrendingUp className="h-3.5 w-3.5" /> {isSwot ? 'Open analytics' : 'Open Fleet Performance'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
};

export default TopAlertStrip;
